/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  useHasPermission
} from '@ion/applications/src/security/useHasPermission';
import {
  EntityName,
  EntityOperation
} from '@ion/core/src/types/entities';
import Features from '@/security/Features';

// [ION-APP TODO]: This module has been deprecated and clients will need
// to be updated to use the `Features API` exclusively, specifically, the
// `Features.hasFeature` method.

/**
 * @deprecated - iOn Monorepo Applications are ONLY to determine Access to Features
 * via the Applications Features. References to `Permissions` is **An Anti-Pattern!**
 * and are **NOT TO BE USED!!!**. For additional information and details, please see:
 * https://calampcorp.atlassian.net/browse/IMO-174 .
 *
 * Provides the application specific `useHasPermission` implementation which is scoped
 * to the ADM `Features` API for validating Permissions access based on the authenticated
 * user's application specific role permissions.
 *
 * This hook simply provides a component specific facade over the ADM `Features` API
 * which simply delegates to the `useHasPermission` hook, passing the given arguments
 * and a reference to the ADM `Features` API.
 *
 * ```typescript
 *  import useHasRolePermission from '@/security/useHasRolePermission';
 *  import {EntityName, EntityOperation}
 *  ...
 *
 *  const hasAccountDelete = useHasRolePermission('account', 'delete');
 *
 *  // or ...
 *  const hasAccountDelete = useHasRolePermission(EntityName.account, EntityOperation.Delete);
 *
 *  // or any combination of the above (params will resolve regardless of case)...
 *  const hasAccountDelete = useHasRolePermission('account', EntityOperation.Delete);
 *  const hasAccountDelete = useHasRolePermission(EntityName.account, 'delete');
 *
 *  // permissions will resolve regardless of case ...
 *  const hasAccountDelete = useHasRolePermission('Account', 'DELETE');
 * ```
 *
 * @param   entity An `Entity` enum value or the string literal entity name.
 * @param   operation An `Operation` enum value or the string literal operation type.
 * @returns `true` if the authenticated user's applications roles includes the given
 *          permission, otherwise, `false`.
 */
export const useHasRolePermission = (
  entity: EntityName, operation: EntityOperation): boolean => (
  useHasPermission(entity, operation, Features)
);
export default useHasRolePermission;
